import { defineStore } from 'pinia';
import { getFreelanceStaff, getFreelanceStaffById, addFreelancerByCode, updateFreelanceStaff } from '@/services/freelanceStaffService';

/** Map API freelance-staff item to shape expected by FreelanceStaffPage and modal (image, name, number, address1/town). */
function mapApiToPageItem(item) {
    if (!item || typeof item !== 'object') return null;
    const addr = item.address || {};
    const address = {
        ...addr,
        address1: addr.address_1 ?? addr.address1,
        address2: addr.address_2 ?? addr.address2,
        town: addr.city ?? addr.town,
        county: addr.state ?? addr.county ?? addr.county
    };
    return {
        id: item.id,
        hashid: item.hashid,
        name: item.name || item.email || '—',
        email: item.email,
        phone: item.phone,
        number: item.phone,
        image: item.image_url,
        image_url: item.image_url,
        description: item.description,
        day_rate: item.day_rate,
        is_bookable: item.is_bookable,
        isBookable: item.is_bookable !== false,
        skills: item.skills || [],
        roles: item.roles || [],
        company: item.company ?? '',
        rating: item.rating ?? null,
        address,
        vat_registered: item.vat_registered,
        vat_number: item.vat_number,
        created_at: item.created_at,
        updated_at: item.updated_at,
        internal_notes: item.internal_notes,
        social_media: item.social_media,
        emergency_contact_name: item.emergency_contact_name,
        emergency_contact_phone: item.emergency_contact_phone,
        emergency_contact_relationship: item.emergency_contact_relationship,
        specialisms: item.specialisms || [],
        certifications: item.certifications || [],
    };
}

export const useFreelanceStore = defineStore('freelancers', {
    state: () => ({
        freelancers: [],
        loading: false,
        error: null,
    }),
    actions: {
        async loadFreelancers(search = '') {
            this.loading = true;
            this.error = null;
            try {
                const raw = await getFreelanceStaff(search);
                const list = Array.isArray(raw) ? raw : [];
                this.freelancers = list.map(mapApiToPageItem).filter(Boolean);
            } catch (err) {
                console.error('[freelanceStaffStore] Error loading freelance staff:', err);
                this.error = err?.message ?? 'Failed to load freelancers';
                this.freelancers = [];
            } finally {
                this.loading = false;
            }
        },
        async getFreelancerById(id) {
            if (id == null || id === '') return null;
            const byId = (c) => String(c.id) === String(id) || c.hashid === id;
            let freelance = this.freelancers.find(byId);
            if (freelance) return freelance;
            try {
                const raw = await getFreelanceStaffById(id);
                freelance = mapApiToPageItem(raw);
                if (freelance) this.freelancers = [...this.freelancers.filter((c) => !byId(c)), freelance];
                return freelance ?? null;
            } catch {
                return null;
            }
        },
        /** Add freelancer to company by share code. Returns { success, data } or { success: false, error, status }. */
        async addByCode(shareCode) {
            const code = String(shareCode ?? '').trim();
            if (!code) return { success: false, error: 'Share code is required', status: 400 };
            try {
                const raw = await addFreelancerByCode(code);
                const freelance = mapApiToPageItem(raw);
                if (freelance) this.freelancers = [freelance, ...this.freelancers];
                return { success: true, data: freelance ?? raw };
            } catch (err) {
                const status = err?.response?.status;
                const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to add freelancer';
                if (status === 404) return { success: false, error: 'No freelancer found with that code.', status: 404 };
                if (status === 409) return { success: false, error: 'This freelancer is already in your company.', status: 409 };
                if (status === 403) return { success: false, error: 'Only company users can add freelancers.', status: 403 };
                return { success: false, error: msg, status: status ?? 500 };
            }
        },
        /** Update freelancer via API. Payload: name, phone, email, description, is_bookable, vat_registered, vat_number, day_rate, address, etc. */
        async updateFreelancer(updatedFreelance) {
            const id = updatedFreelance.id ?? updatedFreelance.hashid;
            if (id == null) return;
            const addr = updatedFreelance.address || {};
            const address = {
                address_1: addr.address_1 ?? addr.address1,
                address_2: addr.address_2 ?? addr.address2,
                city: addr.city ?? addr.town,
                state: addr.state ?? addr.county,
                postcode: addr.postcode,
                country: addr.country
            };
            Object.keys(address).forEach((k) => address[k] === undefined && delete address[k]);
            const payload = {
                name: updatedFreelance.name,
                phone: updatedFreelance.phone ?? updatedFreelance.number,
                email: updatedFreelance.email,
                description: updatedFreelance.description,
                is_bookable: updatedFreelance.is_bookable ?? updatedFreelance.isBookable,
                vat_registered: updatedFreelance.vat_registered,
                vat_number: updatedFreelance.vat_number,
                day_rate: updatedFreelance.day_rate != null ? parseFloat(updatedFreelance.day_rate) : undefined,
                social_media: updatedFreelance.social_media,
                emergency_contact_name: updatedFreelance.emergency_contact_name,
                emergency_contact_phone: updatedFreelance.emergency_contact_phone,
                emergency_contact_relationship: updatedFreelance.emergency_contact_relationship,
                internal_notes: updatedFreelance.internal_notes,
                address: Object.keys(address).length ? address : undefined
            };
            Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
            const raw = await updateFreelanceStaff(id, payload);
            const freelance = mapApiToPageItem(raw);
            const index = this.freelancers.findIndex((c) => String(c.id) === String(id) || c.hashid === id);
            if (index !== -1 && freelance) this.freelancers[index] = { ...this.freelancers[index], ...freelance };
            return freelance;
        },
        async deleteFreelancer(id) {
            this.freelancers = this.freelancers.filter((c) => c.id !== id && c.hashid !== id);
        },
    },
});
